import { assertIsNotUndefined } from "common/utils/assertOfType";
import {
  addOcclusionPolygonToBSP,
  clipPolygonOutsideConvexVol,
  extendedDataPolygonBSPNew,
  getVerticesAABB3,
  polygonBSPNew,
  renderNodeBSP,
  splitPolygonBSP,
} from "geom/bsp";
import type { NodeBSP, PolygonBSP, RenderInfoBSP } from "geom/bspTypes";
import { BSP_EPSILON, TraversalCullingBSP, TraversalOrderBSP } from "geom/bspTypes";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { mat4Dot, mat4Invert, mat4Mult } from "geom/mat4";
import { offsetPolyLine2 } from "geom/offset";
import { planeBSPByPointAndNormal } from "geom/planeBSP";
import { polygonUnion } from "geom/polygon";
import { polyLine2SetCCW, polyLine2MergeCoincidentVertices, polyLine2Area } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { pushXY } from "geom/utils";
import { vec3Normalize } from "geom/vec3";
import type { RenderPerspectiveLinesOptions } from "./perspective";

export interface EntityPolyLine {
  polylines: PolyLine2[];
  entityId: string;
}

export function renderPerspectiveEntityPolygons({
  sceneBSP,
  viewMatrix,
  isPerspectiveMatrix,
  prjMatrix,
  clippingPlanes,
  epsilon = BSP_EPSILON,
  entityTable,
  unionEntityGroups = true,
}: Omit<RenderPerspectiveLinesOptions, "isElevation" | "isPlanView"> & {
  unionEntityGroups?: boolean;
}): EntityPolyLine[] {
  const renderedPolyLines: EntityPolyLine[] = [];

  let occlusionBSP: NodeBSP | null = null;

  const invViewMatrix = mat4Invert(viewMatrix);

  const viewPosX = invViewMatrix[12];
  const viewPosY = invViewMatrix[13];
  const viewPosZ = invViewMatrix[14];

  const [viewX, viewY, viewZ] = vec3Normalize(-viewPosX, -viewPosY, -viewPosZ);
  const finalMatrix = mat4Mult(prjMatrix, viewMatrix);

  const renderInfo: RenderInfoBSP = {
    isPerspectiveMatrix,
    viewX,
    viewY,
    viewZ,
    viewPosX,
    viewPosY,
    viewPosZ,
  };

  function renderNodeFunc(node: NodeBSP, side: number) {
    const { polygons: nodePolygons } = node;

    // iterate all the polygons from this node
    for (let p = 0, pl = nodePolygons.length; p < pl; p++) {
      const polygon = nodePolygons[p];

      // clip the polygon against the view frustum volume (in world space)
      const clippedPolygon = clippingPlanes ? clipPolygonOutsideConvexVol(polygon, clippingPlanes) : polygon;
      if (!clippedPolygon) {
        continue;
      }

      // project the polygon
      const { vertices, extendedData } = clippedPolygon;
      const entityId = extendedData?.entityId;
      const projectedVertices = new Array<number>(vertices.length);
      for (let i = 0, l = vertices.length; i < l; i += 3) {
        const [screenX, screenY] = mat4Dot(finalMatrix, vertices[i], vertices[i + 1], vertices[i + 2]);

        projectedVertices[i] = screenX;
        projectedVertices[i + 1] = screenY;
        projectedVertices[i + 2] = 0;
      }

      // build the projected polygon plane
      const projectedPolygonPlane = planeBSPByPointAndNormal(
        projectedVertices[0],
        projectedVertices[1],
        projectedVertices[2],
        0,
        0,
        1,
      );

      const projectedPolygonExtendedData = extendedDataPolygonBSPNew(
        0,
        getVerticesAABB3(projectedVertices),
        [],
        [],
        [],
        [],
        [],
        undefined,
        entityId,
      );

      // build the projected polygon
      const projectedPolygon = polygonBSPNew(0, projectedVertices, projectedPolygonPlane, projectedPolygonExtendedData);

      // clip the projected polygon against the occlusion BSP
      const frontPolygons: PolygonBSP[] = [];
      splitPolygonBSP({
        node: occlusionBSP,
        polygon: projectedPolygon,
        coplanarsToFront: false,
        frontPolygons,
      });

      // iterate the visible sub-polygons
      const projectedPolyLines: PolyLine2[] = [];
      for (const frontPolygon of frontPolygons) {
        const pts: number[] = [];
        const { vertices } = frontPolygon;

        let firstX: number | undefined;
        let firstY: number | undefined;

        // iterate the visible vertices to create the loop
        for (let i = 0, l = vertices.length; i < l; i += 3) {
          const screenX = vertices[i];
          const screenY = vertices[i + 1];

          if (i === 0) {
            firstX = screenX;
            firstY = screenY;
          }

          // add point to the loop
          pushXY(pts, screenX, screenY);
        }

        // close the loop
        assertIsNotUndefined(firstX);
        assertIsNotUndefined(firstY);
        pushXY(pts, firstX, firstY);

        // clean and enlarge the loop to improve the union algorithm
        const enlargedPoly = offsetPolyLine2(
          polyLine2SetCCW(polyLine2MergeCoincidentVertices(polyLine2New(pts))),
          -DIST_EPSILON,
        );

        // discard the loop if is a degenerated polygon
        if (polyLine2Area(enlargedPoly) < DIST_EPSILON_SQUARED) {
          continue;
        }

        // store the loop
        projectedPolyLines.push(enlargedPoly);

        // update the occlusion BSP with this visible sub-polygon
        if (Math.abs(side) >= BSP_EPSILON) {
          occlusionBSP = addOcclusionPolygonToBSP({
            node: occlusionBSP,
            isPerspectiveMatrix,
            polygon: frontPolygon,
            zfar: 10000,
            epsilon,
          });
        }
      }

      if (projectedPolyLines.length === 0) {
        continue;
      }

      if (unionEntityGroups) {
        let union: PolyLine2[];
        try {
          union = polygonUnion(projectedPolyLines.map((x) => [x])).map((x) => offsetPolyLine2(x[0], DIST_EPSILON));
        } catch (e) {
          const entity = entityTable && entityId ? entityTable.getEntityDescriptor(entityId) : undefined;
          console.warn(
            e,
            "See attached view entity object to get more information on which 3D entity we had trouble projecting to a 2D view.",
            entity,
          );
          continue;
        }

        renderedPolyLines.push({ polylines: union, entityId: entityId ?? "" });
      } else {
        renderedPolyLines.push({ polylines: projectedPolyLines, entityId: entityId ?? "" });
      }
    }
  }

  renderNodeBSP({
    node: sceneBSP.root,
    renderInfo,
    order: TraversalOrderBSP.FRONT_TO_BACK,
    culling: TraversalCullingBSP.BACK,
    renderFunc: renderNodeFunc,
  });

  return renderedPolyLines;
}
